import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CreateJamModal from './CreateJamModal';
import JamSessionModal from './JamSessionModal';
import { Radio } from 'lucide-react';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JamSessionHub = ({ userToken, userId, username, externalSession, externalShowModal, onModalClose, onNavigateToDiscover, setIsDiscoveringForJam, setTargetSessionId, onSelectLocalTrack, onShareToDiscord }) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [publicSessions, setPublicSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  
  // Sub-tab state for Jam Session Hub
  const [activeSubTab, setActiveSubTab] = useState('my-jam');

  useEffect(() => {
    fetchSessions();
  }, []);

  // Handle external session passed from parent (e.g., from Discovery Zone)
  useEffect(() => {
    if (externalSession && externalShowModal) {
      console.log('🎵 JamSessionHub received external session:', externalSession);
      console.log('🎵 Session queue:', externalSession.queue);
      console.log('🎵 Session current_track:', externalSession.current_track);
      setSelectedSession(externalSession);
      setShowSessionModal(true);
    }
  }, [externalSession, externalShowModal]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      // Fetch user's active sessions
      const activeResponse = await axios.get(`${API}/jam/active`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      setActiveSessions(activeResponse.data);

      // Fetch public sessions
      const publicResponse = await axios.get(`${API}/jam/public`);
      setPublicSessions(publicResponse.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = (session) => {
    setSelectedSession(session);
    setShowSessionModal(true);
    fetchSessions(); // Refresh list
  };

  const handleJoinSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/jam/${sessionId}/join`,
        {},
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      setSelectedSession(response.data);
      setShowSessionModal(true);
      fetchSessions(); // Refresh list
    } catch (err) {
      console.error('Failed to join session:', err);
      alert(err.response?.data?.detail || 'Failed to join session');
    }
  };

  const handleOpenSession = (session) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const handleCloseSessionModal = () => {
    setShowSessionModal(false);
    setSelectedSession(null);
    fetchSessions(); // Refresh list when closing
    
    // If this was an external session, notify parent
    if (onModalClose) {
      onModalClose();
    }
  };

  if (loading) {
    return (
      <div className="jam-hub-loading">
        <div className="loading-spinner"></div>
        <p>Loading jam sessions...</p>
      </div>
    );
  }

  return (
    <div className="jam-session-hub">
      {/* Animation Keyframes */}
      <style>{`
        @keyframes discoBallSpin {
          from { transform: perspective(200px) rotateY(0deg); }
          to { transform: perspective(200px) rotateY(360deg); }
        }
        @keyframes discoBallGlow {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 15px rgba(255, 0, 255, 0.5)); }
          50% { filter: drop-shadow(0 0 12px rgba(255, 255, 255, 1)) drop-shadow(0 0 25px rgba(0, 255, 255, 0.7)); }
        }
        @keyframes radioWave1 {
          0%, 100% { opacity: 0.3; }
          33% { opacity: 1; }
        }
        @keyframes radioWave2 {
          0%, 100% { opacity: 0.3; }
          66% { opacity: 1; }
        }
        @keyframes radioWave3 {
          0%, 100% { opacity: 0.3; }
          100% { opacity: 1; }
        }
        @keyframes musicNoteRock {
          0%, 100% { transform: rotate(-12deg); }
          50% { transform: rotate(12deg); }
        }
        /* Mobile responsive styles for Lounge */
        @media (max-width: 768px) {
          .jam-hub-tabs {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          /* Jam Sessions - full width on top row */
          .jam-hub-tabs button:first-child {
            flex: 1 1 100% !important;
            max-width: 100% !important;
            padding: 14px 16px !important;
            font-size: 15px !important;
          }
          /* Radio and HQ - side by side on second row */
          .jam-hub-tabs button:nth-child(2),
          .jam-hub-tabs button:nth-child(3) {
            flex: 1 1 calc(50% - 4px) !important;
            max-width: calc(50% - 4px) !important;
            padding: 12px 8px !important;
            font-size: 13px !important;
          }
          .jam-hub-tabs button span {
            font-size: inherit !important;
          }
          .jam-hub-tabs button svg {
            width: 20px !important;
            height: 20px !important;
          }
          .lounge-title-header {
            font-size: 2rem !important;
            margin-bottom: 0.5rem !important;
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            white-space: nowrap !important;
            gap: 0.5rem !important;
          }
          .lounge-title-header svg {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            flex-shrink: 0 !important;
          }
        }
      `}</style>

      {/* Lounge Title Header - Matches DJ Booth / Producers Corner */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        padding: '0 1rem'
      }}>
        <h2 className="lounge-title-header" style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          margin: '0',
          marginBottom: '0.5rem'
        }}>
          <Radio size={40} style={{ color: '#00ffff', flexShrink: 0 }} />
          <span style={{
            background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>The Lounge</span>
        </h2>
        <p style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '1rem',
          marginBottom: '1rem',
          maxWidth: '600px'
        }}>
          Live listening sessions, community radio & headquarters
        </p>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="jam-hub-tabs" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '20px',
        background: 'rgba(0, 0, 0, 0.3)',
        padding: '6px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: 'center',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {/* My Jam Session(s) Button */}
        <button
          data-tour="lounge-jam-sessions"
          onClick={() => setActiveSubTab('my-jam')}
          style={{
            flex: '1 1 auto',
            maxWidth: '180px',
            padding: '10px 12px',
            background: activeSubTab === 'my-jam' 
              ? 'linear-gradient(135deg, #00ffff 0%, #0080ff 100%)' 
              : 'transparent',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{
            fontSize: '14px',
            display: 'inline-block',
            animation: 'musicNoteRock 1.2s ease-in-out infinite',
            transformOrigin: 'center bottom',
            filter: 'brightness(0) invert(1)',
            opacity: 0.9
          }}>🎵</span>
          <span>Jam Sessions</span>
        </button>

        {/* BEATSEEK Radio Button */}
        <button
          data-tour="lounge-beatseek-radio"
          onClick={() => setActiveSubTab('radio')}
          style={{
            flex: '1 1 auto',
            maxWidth: '180px',
            padding: '10px 12px',
            background: activeSubTab === 'radio' 
              ? 'linear-gradient(135deg, #ff00ff 0%, #8000ff 100%)' 
              : 'transparent',
            border: 'none',
            borderRadius: '8px',
            color: activeSubTab === 'radio' ? '#fff' : '#fff',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            whiteSpace: 'nowrap'
          }}
        >
          {/* Animated Radio Wave Icon - White */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible', color: '#ffffff' }}>
            {/* Center dot */}
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            {/* Wave 1 - innermost */}
            <path 
              d="M8.5 8.5C9.5 7.5 10.7 7 12 7s2.5.5 3.5 1.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave1 1.5s ease-in-out infinite' }}
            />
            <path 
              d="M15.5 15.5C14.5 16.5 13.3 17 12 17s-2.5-.5-3.5-1.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave1 1.5s ease-in-out infinite' }}
            />
            {/* Wave 2 - middle */}
            <path 
              d="M6 6C7.7 4.3 9.8 3.5 12 3.5s4.3.8 6 2.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave2 1.5s ease-in-out infinite', animationDelay: '0.2s' }}
            />
            <path 
              d="M18 18C16.3 19.7 14.2 20.5 12 20.5s-4.3-.8-6-2.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave2 1.5s ease-in-out infinite', animationDelay: '0.2s' }}
            />
            {/* Wave 3 - outermost */}
            <path 
              d="M3.5 3.5C5.8 1.2 8.8 0 12 0s6.2 1.2 8.5 3.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave3 1.5s ease-in-out infinite', animationDelay: '0.4s' }}
            />
            <path 
              d="M20.5 20.5C18.2 22.8 15.2 24 12 24s-6.2-1.2-8.5-3.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
              style={{ animation: 'radioWave3 1.5s ease-in-out infinite', animationDelay: '0.4s' }}
            />
          </svg>
          <span>BEATSEEK Radio</span>
        </button>

        {/* BEATSEEK HQ Button */}
        <button
          data-tour="lounge-beatseek-hq"
          onClick={() => setActiveSubTab('lounge')}
          style={{
            flex: '1 1 auto',
            maxWidth: '180px',
            padding: '10px 12px',
            background: activeSubTab === 'lounge' 
              ? 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' 
              : 'transparent',
            border: 'none',
            borderRadius: '8px',
            color: activeSubTab === 'lounge' ? '#fff' : '#fff',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{
            fontSize: '18px',
            display: 'inline-block',
            animation: 'discoBallSpin 4s linear infinite, discoBallGlow 2s ease-in-out infinite'
          }}>🪩</span>
          <span>BEATSEEK HQ</span>
        </button>
      </div>

      {/* My Jam Session Content */}
      {activeSubTab === 'my-jam' && (
        <>
          {/* Separator with Title and Create Button on same row */}
          <div style={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
            paddingTop: '12px',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {activeSessions.length > 0 && (
              <h3 style={{ margin: 0, fontSize: '16px', color: '#00ffff' }}>Your Active Sessions ({activeSessions.length})</h3>
            )}
            {activeSessions.length === 0 && <div></div>}
            <button 
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #00ffff 0%, #0080ff 100%)',
                border: 'none',
                borderRadius: '6px',
                color: '#000',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              + Create New Session
            </button>
          </div>

          {/* Active Sessions */}
          {activeSessions.length > 0 && (
            <div className="jam-hub-section" style={{ marginTop: '0', paddingTop: '0' }}>
              <div className="jam-session-grid">
                {activeSessions.map(session => (
                  <div key={session.session_id} className="jam-session-card-hub">
                    <div className="jam-session-card-header">
                      <h4>{session.name}</h4>
                      {session.host_user_id === userId && <span className="host-badge">👑 Host</span>}
                    </div>
                    <div className="jam-session-card-info">
                      <p className="jam-host">🎧 Host: {session.host_username}</p>
                      <p className="jam-participants">👥 {session.participants.length} listening</p>
                      {session.current_track && (
                        <p className="jam-now-playing">
                          🎵 Now: {session.current_track.title}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      {onShareToDiscord && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onShareToDiscord({
                              name: session.name,
                              currentTrack: session.current_track?.title || 'Getting started...',
                              listeners: session.participants.length,
                              host: session.host_username,
                              joinUrl: `${window.location.origin}?jam=${session.session_id}`
                            });
                          }}
                          title="Invite via Discord"
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: 'rgba(88, 101, 242, 0.2)',
                            border: '1px solid #5865F2',
                            borderRadius: '6px',
                            color: '#5865F2',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                            fontSize: '12px'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 71 55" fill="#5865F2">
                            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                          </svg>
                          Invite
                        </button>
                      )}
                      <button
                        className="jam-btn jam-btn-primary jam-btn-small"
                        style={{ flex: 2 }}
                        onClick={() => handleOpenSession(session)}
                      >
                        Open Session
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Sessions */}
          <div className="jam-hub-section">
            <h3>🌍 Browse Public Sessions ({publicSessions.length})</h3>
            {publicSessions.length === 0 ? (
              <div className="jam-empty-state">
                <p>🎵</p>
                <p>No public sessions available</p>
                <p className="jam-help-text">Be the first to create one!</p>
              </div>
            ) : (
              <div className="jam-session-grid">
                {publicSessions.map(session => {
                  const isParticipant = session.participants.some(p => p.user_id === userId);
                  return (
                    <div key={session.session_id} className="jam-session-card-hub">
                      <div className="jam-session-card-header">
                        <h4>{session.name}</h4>
                      </div>
                      <div className="jam-session-card-info">
                        <p className="jam-host">🎧 Host: {session.host_username}</p>
                        <p className="jam-participants">👥 {session.participants.length} listening</p>
                        {session.current_track && (
                          <p className="jam-now-playing">
                            🎵 Now: {session.current_track.title}
                          </p>
                        )}
                      </div>
                      <button
                        className="jam-btn jam-btn-primary jam-btn-small"
                        onClick={() => isParticipant ? handleOpenSession(session) : handleJoinSession(session.session_id)}
                      >
                        {isParticipant ? 'Open Session' : 'Join Session'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* BEATSEEK Radio Coming Soon */}
      {activeSubTab === 'radio' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          {/* Animated Radio Icon */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.2) 0%, rgba(128, 0, 255, 0.2) 100%)',
            border: '2px solid rgba(255, 0, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            animation: 'pulse 2s ease-in-out infinite',
            boxShadow: '0 0 40px rgba(255, 0, 255, 0.3)'
          }}>
            <span style={{ fontSize: '48px' }}>📻</span>
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '12px'
          }}>
            BEATSEEK Radio
          </h2>

          {/* Coming Soon Badge */}
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: 'linear-gradient(135deg, #ff00ff 0%, #8000ff 100%)',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '24px'
          }}>
            Coming Soon
          </div>

          {/* Description */}
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            maxWidth: '500px',
            lineHeight: '1.6',
            marginBottom: '32px'
          }}>
            24/7 curated electronic music stations from the best underground and mainstream sources. 
            Live DJ sets, exclusive shows, and genre-specific channels - all in one place.
          </p>

          {/* Features Preview - Simple text, condensed */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '24px',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '14px'
          }}>
            <span>• Live Stations</span>
            <span>• DJ Sets</span>
            <span>• Genre Channels</span>
            <span>• Global Streams</span>
          </div>

          {/* CTA Buttons - Only these have button styling */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #ff00ff 0%, #8000ff 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(255, 0, 255, 0.3)'
            }}>
              🔔 Notify Me When Live
            </button>
            <button style={{
              padding: '12px 24px',
              background: 'transparent',
              border: '1px solid rgba(255, 0, 255, 0.5)',
              borderRadius: '8px',
              color: '#ff00ff',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}>
              📧 Station Partnership
            </button>
          </div>
        </div>
      )}

      {/* BEATSEEK Lounge Coming Soon */}
      {activeSubTab === 'lounge' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          {/* Animated Lounge Icon */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(239, 68, 68, 0.2) 100%)',
            border: '2px solid rgba(245, 158, 11, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            animation: 'pulse 2s ease-in-out infinite',
            boxShadow: '0 0 40px rgba(245, 158, 11, 0.3)'
          }}>
            <span style={{ fontSize: '48px' }}>🛋️</span>
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '12px'
          }}>
            BEATSEEK Lounge
          </h2>

          {/* Coming Soon Badge */}
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '24px'
          }}>
            Coming Soon
          </div>

          {/* Description */}
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            maxWidth: '500px',
            lineHeight: '1.6',
            marginBottom: '16px'
          }}>
            Your weekly virtual nightclub experience. Join us every Saturday night 
            for live DJ sets, community vibes, and exclusive performances.
          </p>

          {/* Schedule Highlight */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            marginBottom: '32px'
          }}>
            <span style={{ fontSize: '20px' }}>📅</span>
            <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '15px' }}>
              Every Saturday Night
            </span>
          </div>

          {/* Features Preview - Simple text, condensed */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '24px',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '14px'
          }}>
            <span>• Live DJ Sets</span>
            <span>• Community Chat</span>
            <span>• Guest Artists</span>
            <span>• Exclusive Content</span>
          </div>

          {/* CTA Buttons - Only these have button styling */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)'
            }}>
              🔔 Notify Me
            </button>
            <button style={{
              padding: '12px 24px',
              background: 'transparent',
              border: '1px solid rgba(245, 158, 11, 0.5)',
              borderRadius: '8px',
              color: '#f59e0b',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}>
              🎧 Apply to DJ
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateJamModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSessionCreated={handleCreateSession}
          userToken={userToken}
        />
      )}

      {showSessionModal && selectedSession && (
        <JamSessionModal
          session={selectedSession}
          userToken={userToken}
          userId={userId}
          username={username}
          onClose={handleCloseSessionModal}
          onNavigateToDiscover={onNavigateToDiscover}
          setIsDiscoveringForJam={setIsDiscoveringForJam}
          setTargetSessionId={setTargetSessionId}
          onSelectLocalTrack={onSelectLocalTrack}
        />
      )}
    </div>
  );
};

export default JamSessionHub;
